import { BookmarkIcon, Loader } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSavedContent } from '../context/savedContent';
import { useAnalytics } from '~/context/analytics';

interface MockSaveButtonProps {
  mockId: string;
  imageId: string | number;
  className?: string;
  variant?: 'default' | 'ghost';
  size?: 'default' | 'sm' | 'icon';
  isSaved?: boolean;
  onSaveChange?: (isSaved: boolean) => void;
}

export function MockSaveButton({
  mockId,
  imageId,
  className,
  variant = 'ghost',
  size = 'icon',
  isSaved = false,
  onSaveChange,
}: MockSaveButtonProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const { handleMockSave } = useSavedContent();
  const [isProcessing, setIsProcessing] = useState(false);
  const { amplitude, intercom } = useAnalytics();

  const handleToggleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      // Optimistically update the UI
      onSaveChange?.(!isSaved);

      if (!isSaved) {
        amplitude.track('mockup_saved');
        intercom.trackEvent('mockup_saved');
      }

      await handleMockSave(imageId.toString(), mockId, !isSaved);
    } catch (error) {
      console.error('Error toggling save:', error);
      toast.error(t('mock.save-error'));
      // Revert the optimistic update
      onSaveChange?.(isSaved);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      onClick={handleToggleSave}
      variant={variant}
      size={size}
      disabled={isProcessing}
      className={clsx(
        'transition-all duration-200 ease-in-out bg-black/70 hover:bg-black/90',
        isSaved ? 'text-white hover:text-white' : 'text-white hover:text-white',
        'w-auto px-4',
        className
      )}
      title={isSaved ? t('mock.unsave-title') : t('mock.save-title')}
    >
      {isProcessing ? (
        <Loader className="h-4 w-4 animate-spin" />
      ) : (
        <div className="flex items-center space-x-2">
          <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : 'fill-none'}`} />
          {!isSaved && <span>{tc('save')}</span>}
        </div>
      )}
    </Button>
  );
}

export default MockSaveButton;
